/**
 * Profile editor modal component
 */
import { useState, useRef } from 'react';
import type { ProfileContent } from '@amorphous/core';
import { Avatar } from './Avatar';
import './ProfileEditor.css';

interface ProfileEditorProps {
  currentProfile?: ProfileContent;
  onSave: (profile: { displayName: string; bio?: string; avatarDataUrl?: string }) => Promise<void>;
  onCancel: () => void;
}

export function ProfileEditor({ currentProfile, onSave, onCancel }: ProfileEditorProps) {
  const [displayName, setDisplayName] = useState(currentProfile?.displayName || '');
  const [bio, setBio] = useState(currentProfile?.bio || '');
  const [avatarDataUrl, setAvatarDataUrl] = useState<string | undefined>(currentProfile?.avatarDataUrl);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAvatarError(null);

    if (file.size > 50 * 1024) {
      setAvatarError('Image must be under 50KB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setAvatarError('File must be an image');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      setAvatarDataUrl(dataUrl);
    };
    reader.onerror = () => {
      setAvatarError('Failed to read image');
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!displayName.trim()) return;
    setSaving(true);
    try {
      await onSave({
        displayName: displayName.trim(),
        bio: bio.trim() || undefined,
        avatarDataUrl,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div className="profile-editor-overlay" onClick={handleOverlayClick}>
      <div className="profile-editor-modal">
        <div className="profile-editor-header">
          <h2>Edit Profile</h2>
          <button className="close-btn" onClick={onCancel}>x</button>
        </div>

        <div className="profile-editor-content">
          <div className="avatar-edit-section">
            <div
              className="avatar-edit-picker"
              onClick={() => fileInputRef.current?.click()}
            >
              <Avatar
                src={avatarDataUrl}
                displayName={displayName || 'U'}
                size="large"
              />
              <div className="avatar-edit-overlay">Change</div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarSelect}
              style={{ display: 'none' }}
            />
            {avatarError && <span className="avatar-error">{avatarError}</span>}
          </div>

          <div className="profile-edit-form">
            <label className="form-label">
              Display Name
              <input
                type="text"
                className="profile-input"
                placeholder="Your display name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={50}
              />
            </label>

            <label className="form-label">
              Bio
              <textarea
                className="profile-input bio-input"
                placeholder="A short bio (optional)"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                maxLength={200}
                rows={3}
              />
            </label>
          </div>
        </div>

        <div className="profile-editor-actions">
          <button className="cancel-btn" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button
            className="save-btn"
            onClick={handleSubmit}
            disabled={!displayName.trim() || saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
